import React, { FunctionComponent, useEffect, useState } from 'react';
import {
  Col,
  Dropdown,
  DropdownButton,
  Row,
} from 'react-bootstrap';
import { ApplicationState } from '../../../redux/States';
import { ErrorDispatches } from '../../../redux/Dispatches';
import { ConnectedProps, connect } from 'react-redux';
import {
  AuthedLayout,
  EditableTableDiv,
  HeaderDisplayKeysWithType,
} from '../../../components';
import {
  StudentWithGradeNo,
  UserInfo,
} from '../../../model';
import apis from '../../../apis';
import {
  downloadDataAsExcel,
  SheetHeaderDisplay,
} from '../../../utils';
import { useSemGrade } from '../../../hook';
import { toast } from 'react-toastify';

const tableHeader: (
  SheetHeaderDisplay<StudentWithGradeNo> &
  HeaderDisplayKeysWithType<StudentWithGradeNo>
)[] = [
    {
      display: '統編',
      property: 'pid',
    },
    {
      display: '年級',
      property: 'grade',
    },
    {
      display: '班級',
      property: 'className',
    },
    {
      display: '學生',
      property: 'name',
    },
    {
      display: '座號',
      property: 'seat',
    },
    {
      display: '性別',
      property: 'sex',
      onRender: (v) => (v === '1' ? '男' : '女'),
      onSheetRender: (v) => (v === '1' ? '男' : '女'),
    },
  ];

const mapState = (app: ApplicationState) => ({
  ...app.auth,
});

const mapDispatch = ErrorDispatches;

const connector = connect(
  mapState,
  mapDispatch
);

type Props = ConnectedProps<typeof connector>;

const aborigine: FunctionComponent<Props> = ({
  user,
  catchErrorForModal,
}) => {
  const {
    yearSem,
    element: semGradeElement,
  } = useSemGrade();

  const [userinfo, setUser] =
    useState<UserInfo>();

  const [students, setStudents] =
    useState<StudentWithGradeNo[]>([]);

  useEffect(() => {
    if (yearSem) {
      toast
        .promise(
          apis
            .GetAborigineStudent(
              yearSem.year,
              yearSem.sem
            )
            .then((r) => {
              setStudents(r);
            }),
          {
            pending: '資料載入中......',
            success: '資料載入成功！',
            error:
              '資料載入失敗！請查看錯誤資訊。',
          }
        )
        .catch(catchErrorForModal);

      apis
        .getUserInfo()
        .then((u) => {
          setUser(u);
        })
        .catch(catchErrorForModal);
    }
  }, [
    yearSem?.year,
    yearSem?.sem,
  ]);

  const title =
    `${user.schools[user.currentSchool].name}` +
    `${yearSem?.year}學年` +
    `${yearSem?.sem}學期_原住民學生清單`;

  const handleExcel = () => {
    downloadDataAsExcel({
      title,
      values: students,
      headers: tableHeader,
      footer:
        '承辦人:　　　　　　　　　　　　　組長:　　　　　　　　　　　　　主任:　　　　　　　　　　　　　　校長:　　　　　　　　　　　　',
      exportfile: userinfo?.exportFileType,
    });
  };

  const handlePrint = () => {
    if (!students.length) return;

    const printWindow = window.open(
      '',
      '_blank',
      'width=900,height=800'
    );

    if (!printWindow) {
      alert(
        '瀏覽器阻擋了列印視窗，請允許彈出式視窗。'
      );
      return;
    }

    const tableRows = students
      .map((student) => {
        const sex =
          student.sex === '1' ? '男' : '女';

        return `
          <tr>
            <td>${student.pid ?? ''}</td>
            <td>${student.grade ?? ''}</td>
            <td>${student.className ?? ''}</td>
            <td>${student.name ?? ''}</td>
            <td>${student.seat ?? ''}</td>
            <td>${sex}</td>
          </tr>
        `;
      })
      .join('');

    printWindow.document.open();

    printWindow.document.write(`
      <!DOCTYPE html>

      <html lang="zh-Hant">

        <head>

          <meta charset="UTF-8" />

          <title>${title}</title>

          <style>

            * {
              box-sizing: border-box;
            }

            html,
            body {
              margin: 0;
              padding: 0;
              font-family:
                "Microsoft JhengHei",
                "Noto Sans TC",
                Arial,
                sans-serif;
            }

            .print-page {
              width: 100%;
              min-height: 277mm;
              display: flex;
              flex-direction: column;
            }

            h1 {
              margin: 0 0 20px;
              text-align: center;
              font-size: 20px;
            }

            table {
              width: 100%;
              border-collapse: collapse;
              table-layout: fixed;
            }

            th,
            td {
              border: 1px solid #000;
              padding: 6px 4px;
              text-align: center;
              vertical-align: middle;
              font-size: 12px;
              word-break: break-all;
            }

            th {
              font-weight: bold;
            }

            th:nth-child(1),
            td:nth-child(1) {
              width: 28%;
            }

            th:nth-child(2),
            td:nth-child(2) {
              width: 12%;
            }

            th:nth-child(3),
            td:nth-child(3) {
              width: 14%;
            }

            th:nth-child(4),
            td:nth-child(4) {
              width: 20%;
            }

            th:nth-child(5),
            td:nth-child(5) {
              width: 12%;
            }

            th:nth-child(6),
            td:nth-child(6) {
              width: 14%;
            }

           .footer {
  margin-top: auto;
  padding-top: 25px;
  font-size: 13px;
  display: flex;
  justify-content: space-between;
  width: 100%;
}

            @page {
              size: A4 portrait;
              margin: 12mm;
            }

            @media print {

              body {
                padding: 0;
              }

              thead {
                display: table-header-group;
              }

              tr {
                page-break-inside: avoid;
              }

            }

          </style>

        </head>

        <body>

          <div class="print-page">

            <h1>
              ${title}
            </h1>

            <table>

              <thead>

                <tr>
                  <th>統編</th>
                  <th>年級</th>
                  <th>班級</th>
                  <th>學生</th>
                  <th>座號</th>
                  <th>性別</th>
                </tr>

              </thead>

              <tbody>
                ${tableRows}
              </tbody>

            </table>

          <div class="footer">
  <span>承辦人:</span>
  <span>組長:</span>
  <span>主任:</span>
  <span>校長:</span>
</div>
          </div>

          <script>

            window.onload = function () {

              window.focus();

              setTimeout(function () {
                window.print();
              }, 300);

            };

            window.onafterprint = function () {
              window.close();
            };

          </script>

        </body>

      </html>
    `);

    printWindow.document.close();
  };

  return (
    <AuthedLayout>

      <Row className="justify-content-between">

        <Col>

          <Row>

            <Col
              xs={3}
              className="mr-3"
            >
              {semGradeElement}
            </Col>

          </Row>

        </Col>

        <div>

          <DropdownButton
            id="aborigine-export"
            title="匯出"
            variant="success"
            disabled={!students.length}
          >

            <Dropdown.Item
              onClick={handleExcel}
            >
              Excel 下載
            </Dropdown.Item>

            <Dropdown.Item
              onClick={handlePrint}
            >
              列印
            </Dropdown.Item>

          </DropdownButton>

        </div>

      </Row>

      <hr />

      <Row>

        <EditableTableDiv
          headers={tableHeader}
          values={students}
        />

      </Row>

    </AuthedLayout>
  );
};

export const Aborigine =
  connector(aborigine);